import { RecurrenceUnit } from '../../types';

/**
 * A schedule is stored as an interval unit plus a count, so the familiar
 * presets are just combinations of the two: quarterly is monthly with a count
 * of 3. Anything the presets do not cover is entered as a custom count.
 */
export interface RecurrencePreset {
  value: string;
  label: string;
  interval: RecurrenceUnit | null;
  count: number | null;
}

export const RECURRENCE_PRESETS: RecurrencePreset[] = [
  { value: 'daily', label: 'Daily', interval: 'daily', count: 1 },
  { value: 'weekly', label: 'Weekly', interval: 'weekly', count: 1 },
  { value: 'monthly', label: 'Monthly', interval: 'monthly', count: 1 },
  { value: 'quarterly', label: 'Quarterly (every 3 months)', interval: 'monthly', count: 3 },
  { value: 'biannual', label: 'Biannually (every 6 months)', interval: 'monthly', count: 6 },
  { value: 'yearly', label: 'Yearly', interval: 'yearly', count: 1 },
  { value: 'custom', label: 'Custom', interval: null, count: null },
];

export const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export function monthName(month?: number | null): string {
  return MONTH_OPTIONS.find((option) => option.value === month)?.label ?? '';
}

/** Which preset a saved interval and count came from, or custom if none fits. */
export function presetFor(interval?: RecurrenceUnit | null, count?: number | null): string {
  if (!interval) {
    return 'monthly';
  }
  const steps = count ?? 1;
  const match = RECURRENCE_PRESETS.find(
    (preset) => preset.interval === interval && preset.count === steps,
  );
  return match ? match.value : 'custom';
}

const UNIT_NOUNS: Record<RecurrenceUnit, [string, string]> = {
  daily: ['day', 'days'],
  weekly: ['week', 'weeks'],
  monthly: ['month', 'months'],
  yearly: ['year', 'years'],
};

/** Plain-language summary of a schedule, for read-only views. */
export function describeRecurrence(
  interval?: RecurrenceUnit | null,
  count?: number | null,
  seasonStartMonth?: number | null,
  seasonEndMonth?: number | null,
): string {
  if (!interval) {
    return 'Not set';
  }

  const steps = count ?? 1;
  const preset = RECURRENCE_PRESETS.find(
    (option) => option.interval === interval && option.count === steps && option.value !== 'custom',
  );
  const [singular, plural] = UNIT_NOUNS[interval] ?? ['period', 'periods'];
  const base = preset ? preset.label : `Every ${steps} ${steps === 1 ? singular : plural}`;

  if (seasonStartMonth && seasonEndMonth) {
    return `${base}, ${monthName(seasonStartMonth)} to ${monthName(seasonEndMonth)}`;
  }
  return base;
}
